import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import EnquiryForm from "./EnquiryForm";
import { FaArrowRight, FaPlay, FaChevronDown } from "react-icons/fa";

export default function Hero() {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const enableSound = () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.play().catch(e => console.log("Audio play failed:", e));
        document.removeEventListener("click", enableSound);
      }
    };

    document.addEventListener("click", enableSound);

    return () => {
      document.removeEventListener("click", enableSound);
    };
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden flex items-center justify-center bg-black">
      {/* Video Background with Cinematic Treatment */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          className="absolute top-0 left-0 w-full h-full object-cover scale-105 animate-subtle-zoom"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/assets/images/dubai-poster.jpg"
          style={{ animation: 'subtleZoom 20s infinite alternate' }}
        >
          <source src="/assets/video/dubai.webm" type="video/webm" />
          <source src="/assets/video/dubai.mp4" type="video/mp4" />
        </video>

        {/* Advanced Gradient Overlays */}
        <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30 opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent" />
      </div>

      <div className="container relative z-10 px-6 mx-auto h-full flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 w-full items-center">

          {/* Hero Content - Left Side */}
          <div className="lg:col-span-7 flex flex-col items-start text-left pt-20 lg:pt-0">



            {/* Main Headline */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] font-bold text-white leading-[1] mb-8 drop-shadow-2xl animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Redefining <br />
              <span className="relative inline-block">
                <span className="absolute -inset-1 bg-gradient-to-r from-[#c8a45e]/20 to-transparent blur-xl"></span>
                <span className="relative text-transparent bg-clip-text bg-gradient-to-r from-[#e8d5a3] via-[#c8a45e] to-[#a07d3a] animate-shimmer" style={{ backgroundSize: '200% auto' }}>
                  Luxury Living
                </span>
              </span>
              <br /> in Dubai
            </h1>

            {/* Subheading */}
            <p className="font-body text-lg sm:text-xl text-gray-300 max-w-2xl leading-relaxed mb-12 drop-shadow-md border-l-2 border-[#c8a45e]/50 pl-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Curating the finest selection of off-plan investments, waterfront villas, and penthouses from the region's most prestigious developers.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <button
                onClick={() => navigate("/properties/residential")}
                className="group relative px-10 py-5 bg-[#c8a45e] text-black font-display text-sm font-bold uppercase tracking-[0.15em] rounded-none overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(200,164,94,0.4)]"
              >
                <div className="absolute inset-0 w-full h-full bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                <span className="relative z-10 flex items-center gap-3">
                  Explore Collection <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                </span>
              </button>

              <button
                onClick={() => navigate("/contact")}
                className="group px-10 py-5 bg-transparent border border-white/30 text-white font-display text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-black transition-all duration-300 hover:border-white"
              >
                Book Consultation
              </button>
            </div>

            {/* Stats / Trust Indicators */}
            <div className="mt-16 flex items-center gap-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="text-center sm:text-left">
                <p className="font-display text-3xl font-bold text-white mb-1">AED 5Bn+</p>
                <p className="text-[0.65rem] text-gray-400 uppercase tracking-[0.2em]">Sales Volume</p>
              </div>
              <div className="w-px h-10 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
              <div className="text-center sm:text-left">
                <p className="font-display text-3xl font-bold text-white mb-1">500+</p>
                <p className="text-[0.65rem] text-gray-400 uppercase tracking-[0.2em]">Exclusive Units</p>
              </div>
              <div className="w-px h-10 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden sm:block"></div>
              <div className="hidden sm:block">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex -space-x-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full border border-[#0a0a0a] bg-gray-700 overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-600 to-gray-800" />
                      </div>
                    ))}
                  </div>
                  <span className="text-white font-display text-sm font-bold">4.9/5</span>
                </div>
                <p className="text-[0.65rem] text-gray-400 uppercase tracking-[0.2em]">Client Satisfaction</p>
              </div>
            </div>

          </div>

          {/* Enquiry Form - Right Side */}
          <div className="hidden lg:block lg:col-span-5 animate-fade-in-up z-20" style={{ animationDelay: '0.6s' }}>
            <div className="relative transform hover:-translate-y-2 transition-transform duration-500">
              <EnquiryForm />
            </div>
          </div>

        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce cursor-pointer z-10 opacity-70 hover:opacity-100 transition-opacity">
        <span className="text-[0.65rem] text-white uppercase tracking-[0.2em]">Scroll</span>
        <FaChevronDown className="text-[#c8a45e] text-sm" />
      </div>

      {/* Global CSS for custom subtle zoom animation if needed, though mostly handled by tailwind config extension or inline styles */}
      <style>{`
        @keyframes subtleZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }
      `}</style>
    </section>
  );
}
